/** Feature-scoped adapters; imported only by the surfaces that use them. */
import * as React from "react";
import { useApiClient } from "@authe/ui-core/api";
import { createUsageApi, type UsageApi } from "@authe/fe-commons/plans";
import { createAuditSource, type AuditSource } from "../auditSource";
import { createApiKeysSource } from "../apiKeysSource";
import { createApiTenancySource } from "../tenancySource";
import { createApiOrgUsersSource } from "../orgUsersSource";
import { createApiEvidencePolicySource } from "../evidencePolicySource";
import { createApiFeaturesSource } from "../featuresSource";
import { createApiPoliciesSource } from "../policiesSource";

/**
 * The customer feature-flags port — the signed-in tenancy's resolved flags from
 * management-api `GET /features`. Read once per tenancy by
 * `app/DashboardFeatureFlags`, which feeds the flag store; components read a
 * flag with `useFeatureFlag(name)`, never this port (see `./featuresSource`).
 */
export function useFeaturesSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiFeaturesSource(client), [client]);
}

/**
 * The enforced-policy port — the signed-in tenancy's visibility policy from
 * management-api `GET /policies`. Read once per tenancy by `app/enforcedPolicy`,
 * which narrows the policy table every surface reads; components never read
 * this port (see `./policiesSource`).
 */
export function usePoliciesSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiPoliciesSource(client), [client]);
}

/**
 * The confidence-policy port — read the whole ladder (every rung + provenance)
 * and author this scope's slice of it. The sibling of `useProtocolsSource`: that
 * one tunes HOW a protocol decodes, this one tunes how sure we have to be before
 * we act on what it decoded.
 */
export function useEvidencePolicySource() {
  const client = useApiClient();
  return React.useMemo(() => createApiEvidencePolicySource(client), [client]);
}

/**
 * The `management-api` tenancy port (organizations + the tenants under the
 * active org). Backs the Settings "Organizations & tenants" section and the
 * top-bar tenant switcher's friendly names. Speaks canonical `/organizations/*`
 * paths (rewritten onto the `.management.` zone by `RESOURCE_REWRITES`) — see
 * `./tenancySource`.
 */
export function useTenancySource() {
  const client = useApiClient();
  return React.useMemo(() => createApiTenancySource(client), [client]);
}

/**
 * The `management-api` API-keys port — the caller's own keys and the
 * workspace's shared ones. Speaks canonical `/api-keys` paths (rewritten onto
 * the `.management.` zone by `RESOURCE_REWRITES`) and names no tenancy, which is
 * what makes it a dashboard port rather than an admin one — see
 * `./apiKeysSource`.
 */
export function useApiKeysSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiKeysSource(client), [client]);
}

/**
 * The `permissions-mgmt-api` org-user port (organization members + org role
 * catalog + invite). Backs the Team page's "Organization members" list and the
 * "Invite people" dialog. Speaks the dashboard-local canonical prefixes
 * `/org-users[/invite]` + `/org-roles` (rewritten onto the `.management.`
 * permissions zone in `app/api-client.ts`) — see `./orgUsersSource`.
 */
export function useOrgUsersSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiOrgUsersSource(client), [client]);
}

/**
 * The metering read port (`management-api` `GET /usage`). Backs every surface
 * that reports what was actually consumed: the billing page's live figures
 * against the plan's caps, the run console's per-run panel
 * (`?pipeline_run_id=`), and the activity ledger.
 *
 * The adapter itself lives in `@authe/fe-commons/plans` rather than in
 * `src/api/`, because the admin portal reads the same endpoint for its
 * "who used what" breakdown and neither app should own the wire shape of a
 * number a customer is billed from.
 *
 * The caller is never a parameter — `customer_id` comes from the verified auth
 * context server-side.
 */
export function useUsageApi(): UsageApi {
  const client = useApiClient();
  return React.useMemo(() => createUsageApi(client), [client]);
}

/**
 * The audit read port (`management-api` `GET /audit`) — the tenancy's raw call
 * trail, behind Activity → Audit.
 *
 * The other half of the Activity page: the notification inbox carries the
 * events a service chose to ANNOUNCE, this carries every invocation the
 * platform recorded. Dashboard-local rather than shared, because it answers one
 * tenant's question about its own workspace — the admin portal's equivalent
 * reads across customers and is a different surface.
 *
 * The tenancy is never a parameter: it comes from the verified auth context
 * server-side, so this port cannot be pointed at another workspace.
 */
export function useAuditSource(): AuditSource {
  const client = useApiClient();
  return React.useMemo(() => createAuditSource(client), [client]);
}
